/**
 * Utility functions for converting technical feature usage reasons 
 * to user-friendly messages with actionable guidance
 */

export type FeatureUsageReason =
    | 'allowed_within_bundle'
    | 'allowed_overage_charged'
    | 'allowed_unlimited_no_tracking'
    | 'denied_invalid_context'
    | 'denied_not_task_creator'
    | 'denied_feature_not_in_plan'
    | 'denied_limit_exceeded_no_overage_option'
    | 'denied_insufficient_credits_for_overage'
    | 'denied_overage_calculation_error'
    | 'denied_usage_record_error'
    | 'denied_unknown_error';

export interface FeatureUsageMessage {
    title: string;
    description: string;
    actionText?: string;
    actionUrl?: string;
    variant: 'destructive' | 'warning' | 'info';
}

/**
 * Maps feature usage reasons to user-friendly messages
 */
export function getFeatureUsageMessage(
    reason: FeatureUsageReason,
    featureKey?: string,
    context?: {
        remainingInBundle?: number;
        consumedInPeriod?: number;
        limitForPeriod?: number;
        chargedCredits?: number;
    }
): FeatureUsageMessage {
    const featureName = getFeatureFriendlyName(featureKey);

    switch (reason) {
        case 'allowed_within_bundle':
            return {
                title: 'Success',
                description: `${featureName} used successfully. ${context?.remainingInBundle || 0} remaining in your plan.`,
                variant: 'info'
            };

        case 'allowed_overage_charged':
            const creditText = context?.chargedCredits ? ` (${context.chargedCredits} credits charged)` : '';
            return {
                title: 'Overage Charged',
                description: `${featureName} used successfully${creditText}. You've exceeded your plan limit.`,
                actionText: 'View Usage',
                actionUrl: '/credits',
                variant: 'warning'
            };

        case 'allowed_unlimited_no_tracking':
            return {
                title: 'Success',
                description: `${featureName} used successfully. Unlimited usage available.`,
                variant: 'info'
            };

        case 'denied_limit_exceeded_no_overage_option':
            return {
                title: 'Monthly Limit Reached',
                description: getFeatureLimitMessage(featureKey),
                actionText: 'Upgrade Plan',
                actionUrl: '/subscriptions',
                variant: 'destructive'
            };

        case 'denied_insufficient_credits_for_overage':
            return {
                title: 'Insufficient Credits',
                description: `You don't have enough credits to use ${featureName}. Please purchase more credits to continue.`,
                actionText: 'Buy Credits',
                actionUrl: '/credits',
                variant: 'destructive'
            };

        case 'denied_feature_not_in_plan':
            return {
                title: 'Feature Not Available',
                description: `${featureName} is not included in your current plan. Please upgrade to access this feature.`,
                actionText: 'Upgrade Plan',
                actionUrl: '/subscriptions',
                variant: 'destructive'
            };

        case 'denied_not_task_creator':
            return {
                title: 'Permission Denied',
                description: 'Task created by team member, Only the task creator may request AI solutions for this task.',
                actionText: 'View Task Creator',
                actionUrl: '/manage-team',
                variant: 'warning'
            };

        case 'denied_invalid_context':
            return {
                title: 'Access Error',
                description: 'There was an issue with your account permissions. Please try logging out and back in.',
                actionText: 'Contact Support',
                actionUrl: '/support',
                variant: 'destructive'
            };

        case 'denied_overage_calculation_error':
        case 'denied_usage_record_error':
            return {
                title: 'System Error',
                description: 'There was a temporary issue processing your request. Please try again in a moment.',
                actionText: 'Contact Support',
                actionUrl: '/support',
                variant: 'destructive'
            };

        case 'denied_unknown_error':
        default:
            return {
                title: 'Unexpected Error',
                description: 'An unexpected error occurred. Please try again or contact support if the issue persists.',
                actionText: 'Contact Support',
                actionUrl: '/support',
                variant: 'destructive'
            };
    }
}

/**
 * Gets user-friendly feature names
 */
function getFeatureFriendlyName(featureKey?: string): string {
    switch (featureKey) {
        case 'reports':
            return 'PageSpeed Reports';
        case 'normal_prompts':
            return 'AI Chat';
        case 'premium_prompts':
            return 'Advanced AI Features';
        case 'agent_compute_units':
            return 'Agent Compute Units';
        case 'competitor_reports':
            return 'Competitor Analysis';
        case 'monitoring_metrics':
            return 'Website Monitoring';
        case 'team_members_limit':
            return 'Team Members';
        default:
            return featureKey?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'This feature';
    }
}

/**
 * Gets specific limit messages for different features
 */
function getFeatureLimitMessage(featureKey?: string): string {
    switch (featureKey) {
        case 'reports':
            return 'You have reached your monthly report limit. Upgrade your plan or purchase credits to generate more reports.';
        case 'normal_prompts':
            return 'You have reached your monthly AI chat limit. Upgrade your plan or purchase credits to continue using AI assistance.';
        case 'premium_prompts':
            return 'You have reached your monthly premium AI limit. Upgrade your plan or purchase credits to continue using advanced AI features.';
        case 'agent_compute_units':
            return 'You have reached your monthly agent compute limit. Upgrade your plan or purchase credits to continue using the agent.';
        case 'competitor_reports':
            return 'You have reached your monthly competitor analysis limit. Upgrade your plan or purchase credits to continue competitor research.';
        case 'monitoring_metrics':
            return 'You have reached your monthly monitoring limit. Upgrade your plan or purchase credits to continue monitoring your website.';
        case 'team_members_limit':
            return 'You have reached your team member limit. Upgrade your plan to add more team members.';
        default:
            const friendlyName = getFeatureFriendlyName(featureKey);
            return `You have reached your monthly limit for ${friendlyName}. Upgrade your plan or purchase credits to continue.`;
    }
}

/**
 * Helper function to show feature usage toast messages
 */
export function showFeatureUsageToast(
    reason: FeatureUsageReason,
    featureKey?: string,
    context?: {
        remainingInBundle?: number;
        consumedInPeriod?: number;
        limitForPeriod?: number;
        chargedCredits?: number;
    },
    toastFunction?: any,
    navigate?: (path: string) => void
) {
    const message = getFeatureUsageMessage(reason, featureKey, context);
    
    if (!toastFunction) return message;

    const toastOptions: any = {
        description: message.description
    };

    if (message.actionText && message.actionUrl) {
        toastOptions.action = {
            label: message.actionText,
            onClick: () => {
                if (navigate) {
                    navigate(message.actionUrl!);
                } else {
                    // Fallback to window location if navigate is not provided
                    window.location.href = message.actionUrl!;
                }
            }
        };
    }

    if (message.variant === 'destructive') {
        toastFunction.error(message.title, toastOptions);
    } else if (message.variant === 'warning') {
        toastFunction.warning(message.title, toastOptions);
    } else {
        toastFunction.info(message.title, toastOptions);
    }

    return message;
}

/**
 * React hook for handling feature usage messages
 */
export function useFeatureUsageMessage() {
    return {
        getFeatureUsageMessage,
        showFeatureUsageToast,
        getFeatureFriendlyName
    };
}
